import type {
  SmartCreationReviewAdGroup,
  SmartCreationReviewCampaign,
  SmartCreationReviewEditChange,
  SmartCreationReviewNamingRule,
  SmartCreationReviewWorkspace,
} from "@adpilot/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ReviewChangeHandler } from "./review-change-handler";

export type ReviewNamingWorkspace = Pick<
  SmartCreationReviewWorkspace,
  "graph" | "suggestedDraft" | "workspaceRevision" | "contentHash" | "status"
> & {
  planSettings: Pick<SmartCreationReviewWorkspace["planSettings"], "ref" | "namingRule">;
};
export interface ReviewNamingTemplate {
  campaign: string;
  adGroup: string;
}
export type ReviewNamingRule =
  | { rule: "system" }
  | { rule: "custom"; template: ReviewNamingTemplate };
type NameIssue = "empty" | "tooLong" | "unknownToken" | "duplicate";
interface NamePreviewRow {
  ref: SmartCreationReviewCampaign["ref"] | SmartCreationReviewAdGroup["ref"];
  scope: string;
  current: string;
  next: string;
  preserved: boolean;
  issue: NameIssue | null;
  inScope: boolean;
}
interface NamingPreview {
  rows: NamePreviewRow[];
  changes: NamingChange[];
  hasErrors: boolean;
  templateErrors: { campaign: NameIssue | null; adGroup: NameIssue | null };
  tooManyChanges: boolean;
}
type NamingChange = Extract<
  SmartCreationReviewEditChange,
  { operation: "set_campaign_name" | "set_ad_group_name" }
>;
const TOKENS = ["sku", "asin", "strategy", "match", "adGroup", "counter"] as const;
type NamingContext = Record<(typeof TOKENS)[number], string>;
interface NamingContextSource {
  targetsByGroup: Map<string, ReviewNamingWorkspace["graph"]["targets"]>;
  baselineNames: Map<string, string>;
}

function formatTemplate(template: string, context: NamingContext) {
  return template
    .replace(/\{([^{}]+)\}/g, (_, token: string) =>
      TOKENS.includes(token as keyof NamingContext)
        ? context[token as keyof NamingContext]
        : `{${token}}`,
    )
    .trim();
}

function templateIssue(template: string): NameIssue | null {
  if (!template.trim()) return "empty";
  const remainder = template.replace(/\{([^{}]+)\}/g, (_, token: string) =>
    TOKENS.includes(token as keyof NamingContext) ? "" : `{${token}}`,
  );
  return /[{}]/.test(remainder) ? "unknownToken" : null;
}

function contextFor(
  source: NamingContextSource,
  groups: SmartCreationReviewAdGroup[],
  counter: number,
): NamingContext {
  const targets = groups.flatMap((group) => source.targetsByGroup.get(group.ref.lineageKey) ?? []);
  const sortedValues = (values: string[]) => [...new Set(values)].sort().join("+");
  return {
    sku: sortedValues(groups.flatMap((group) => group.products.map((product) => product.sku))),
    asin: sortedValues(groups.flatMap((group) => group.products.map((product) => product.asin))),
    strategy: sortedValues(targets.map((target) => target.strategyKind)),
    match: sortedValues(
      targets.map((target) =>
        target.type === "keyword"
          ? target.matchType.effective
          : target.type === "product"
            ? `ASIN${target.matchType === "expanded" ? "-expanded" : ""}`
            : target.type,
      ),
    ),
    // Initial names avoid recursively appending a template to the result of its previous application.
    adGroup: sortedValues(
      groups.map((group) => source.baselineNames.get(group.ref.itemKey) ?? group.name.effective),
    ),
    counter: String(counter),
  };
}

/** Produces journal commands for the canonical Review graph; previewing never mutates it. */
export function previewReviewNamingRules(
  workspace: ReviewNamingWorkspace,
  rule: ReviewNamingRule,
  overwriteEdited = false,
  selectedCampaignKeys?: ReadonlySet<string>,
  selectedEntityKeys?: ReadonlySet<string>,
): NamingPreview {
  const rows: NamePreviewRow[] = [];
  const campaigns = workspace.graph.campaigns.filter((campaign) => !campaign.excluded.effective);
  const campaignKeys = new Set(campaigns.map((campaign) => campaign.ref.lineageKey));
  const groups = workspace.graph.adGroups.filter((group) =>
    campaignKeys.has(group.campaignLineageKey),
  );
  const baseNames = new Map<string, string>(
    workspace.suggestedDraft.campaigns.flatMap((campaign) => [
      [campaign.itemKey, campaign.name] as const,
      ...campaign.adGroups.map((group) => [group.itemKey, group.name] as const),
    ]),
  );
  const targetsByGroup: NamingContextSource["targetsByGroup"] = new Map();
  for (const target of workspace.graph.targets) {
    const targets = targetsByGroup.get(target.adGroupLineageKey) ?? [];
    targets.push(target);
    targetsByGroup.set(target.adGroupLineageKey, targets);
  }
  const contextSource = { targetsByGroup, baselineNames: baseNames };
  const templateErrors =
    rule.rule === "custom"
      ? {
          campaign: templateIssue(rule.template.campaign),
          adGroup: templateIssue(rule.template.adGroup),
        }
      : { campaign: null, adGroup: null };
  const add = (
    entity: SmartCreationReviewCampaign | SmartCreationReviewAdGroup,
    context: NamingContext,
    scope: string,
    campaignKey: string,
  ) => {
    const inScope =
      (!selectedCampaignKeys?.size || selectedCampaignKeys.has(campaignKey)) &&
      (!selectedEntityKeys || selectedEntityKeys.has(entity.ref.lineageKey));
    const initialName = baseNames.get(entity.ref.itemKey);
    const name =
      rule.rule === "system"
        ? initialName
        : formatTemplate(
            rule.template[entity.ref.kind === "campaign" ? "campaign" : "adGroup"],
            context,
          );
    const preserved =
      !inScope ||
      name === null ||
      name === undefined ||
      (!overwriteEdited && (initialName === undefined || entity.name.effective !== initialName));
    const next = preserved ? entity.name.effective : name.trim();
    rows.push({
      ref: entity.ref,
      scope,
      current: entity.name.effective,
      next,
      preserved,
      inScope,
      issue: !next ? "empty" : next.length > 128 ? "tooLong" : null,
    });
  };
  campaigns.forEach((campaign, index) => {
    add(
      campaign,
      contextFor(
        contextSource,
        groups.filter((group) => group.campaignLineageKey === campaign.ref.lineageKey),
        index + 1,
      ),
      "campaigns",
      campaign.ref.lineageKey,
    );
  });
  groups.forEach((group, index) => {
    add(
      group,
      contextFor(contextSource, [group], index + 1),
      group.campaignLineageKey,
      group.campaignLineageKey,
    );
  });
  const nameBuckets = new Map<string, NamePreviewRow[]>();
  for (const row of rows) {
    const bucket = `${row.ref.kind}:${row.scope}:${row.next.normalize("NFC").toLowerCase()}`;
    const matches = nameBuckets.get(bucket) ?? [];
    matches.push(row);
    nameBuckets.set(bucket, matches);
  }
  for (const duplicates of nameBuckets.values())
    if (duplicates.length > 1) for (const row of duplicates) row.issue = "duplicate";
  const scopedRows = rows.filter((row) => row.inScope);
  const hasNameErrors = Boolean(
    templateErrors.campaign || templateErrors.adGroup || scopedRows.some((row) => row.issue),
  );
  const changes: NamingChange[] = hasNameErrors
    ? []
    : rows
        .filter((row) => !row.preserved && row.next !== row.current)
        .map((row) =>
          row.ref.kind === "campaign"
            ? { operation: "set_campaign_name", ref: row.ref, name: row.next }
            : { operation: "set_ad_group_name", ref: row.ref, name: row.next },
        );
  const tooManyChanges = changes.length > 100;
  return {
    rows: scopedRows,
    changes,
    hasErrors: hasNameErrors || tooManyChanges,
    templateErrors,
    tooManyChanges,
  };
}

export interface ReviewNamingRulesProps {
  workspace: ReviewNamingWorkspace;
  disabled: boolean;
  onApply: ReviewChangeHandler;
  onDirtyChange?(dirty: boolean): void;
  selectedCampaignKeys?: ReadonlySet<string>;
  open?: boolean;
  onOpenChange?(open: boolean): void;
}

const DEFAULT_TEMPLATE: ReviewNamingTemplate = {
  campaign: "AdPilot {sku} {strategy} {match} {counter}",
  adGroup: "{sku} {strategy} {match} {counter}",
};

/** Templates belong to this Review Plan, not Workspace-wide preferences. */
export function ReviewNamingRules({
  workspace,
  disabled,
  onApply,
  onDirtyChange,
  selectedCampaignKeys,
  open: controlledOpen,
  onOpenChange,
}: ReviewNamingRulesProps) {
  const { t } = useTranslation("smartCreation");
  const [savedTemplate, setSavedTemplate] = useState<ReviewNamingTemplate | null>(
    workspace.planSettings.namingRule?.customTemplate ?? null,
  );
  const [selectedRule, setSelectedRule] = useState<"system" | "custom">(
    workspace.planSettings.namingRule?.selectedRule ?? "system",
  );
  const [session, setSession] = useState<{
    snapshot: ReviewNamingWorkspace;
    rule: "system" | "custom";
    selectedCampaignKeys?: ReadonlySet<string>;
    campaignScope: string;
    entityScope: string;
  } | null>(null);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [overwriteEdited, setOverwriteEdited] = useState(false);
  const [pending, setPending] = useState(false);
  const dirtyCallback = useRef(onDirtyChange);
  dirtyCallback.current = onDirtyChange;
  const persistedRule: SmartCreationReviewNamingRule = workspace.planSettings.namingRule ?? {
    selectedRule: "system",
    customTemplate: null,
  };
  const requestedRule: SmartCreationReviewNamingRule = {
    selectedRule: session?.rule ?? selectedRule,
    customTemplate: session?.rule === "custom" ? template : savedTemplate,
  };
  const ruleChanged = JSON.stringify(requestedRule) !== JSON.stringify(persistedRule);
  const rule: ReviewNamingRule =
    session?.rule === "custom" ? { rule: "custom", template } : { rule: "system" };
  const scopeCampaigns = (session?.snapshot.graph.campaigns ?? []).filter(
    (campaign) =>
      !campaign.excluded.effective &&
      (!session?.selectedCampaignKeys?.size ||
        session.selectedCampaignKeys.has(campaign.ref.lineageKey)),
  );
  const scopeCampaignKeys =
    session?.campaignScope === "all"
      ? session.selectedCampaignKeys
      : session
        ? new Set([session.campaignScope])
        : undefined;
  const scopedCampaigns = scopeCampaigns.filter(
    (campaign) => !scopeCampaignKeys?.size || scopeCampaignKeys.has(campaign.ref.lineageKey),
  );
  const scopedGroupKeys = new Set(scopedCampaigns.map((campaign) => campaign.ref.lineageKey));
  const scopeGroups = (session?.snapshot.graph.adGroups ?? []).filter((group) =>
    scopedGroupKeys.has(group.campaignLineageKey),
  );
  const scopeEntityKeys =
    session?.entityScope === "campaigns"
      ? new Set(scopedCampaigns.map((campaign) => campaign.ref.lineageKey))
      : session && session.entityScope !== "all"
        ? new Set([session.entityScope])
        : undefined;
  const preview = session
    ? previewReviewNamingRules(
        session.snapshot,
        rule,
        overwriteEdited,
        scopeCampaignKeys,
        scopeEntityKeys,
      )
    : null;
  const stale = Boolean(
    session &&
      (session.snapshot.workspaceRevision !== workspace.workspaceRevision ||
        session.snapshot.contentHash !== workspace.contentHash),
  );
  const dirty = Boolean(
    session &&
      (overwriteEdited ||
        session.campaignScope !== "all" ||
        session.entityScope !== "all" ||
        (session.rule === "custom" &&
          (template.campaign !== (savedTemplate ?? DEFAULT_TEMPLATE).campaign ||
            template.adGroup !== (savedTemplate ?? DEFAULT_TEMPLATE).adGroup))),
  );
  useEffect(() => {
    dirtyCallback.current?.(dirty);
  }, [dirty]);
  useEffect(() => () => dirtyCallback.current?.(false), []);
  useEffect(() => {
    setSavedTemplate(workspace.planSettings.namingRule?.customTemplate ?? null);
    setSelectedRule(workspace.planSettings.namingRule?.selectedRule ?? "system");
  }, [workspace.planSettings.namingRule]);
  const open = useCallback(
    (nextRule: "system" | "custom") => {
      setTemplate(savedTemplate ?? DEFAULT_TEMPLATE);
      setOverwriteEdited(false);
      setSession({
        snapshot: workspace,
        rule: nextRule,
        campaignScope: "all",
        entityScope: "all",
        selectedCampaignKeys: selectedCampaignKeys?.size
          ? new Set(selectedCampaignKeys)
          : undefined,
      });
    },
    [savedTemplate, workspace, selectedCampaignKeys],
  );
  useEffect(() => {
    if (controlledOpen === true && session === null) open(selectedRule);
    if (controlledOpen === false && session !== null && !pending) setSession(null);
  }, [controlledOpen, session, selectedRule, pending, open]);
  const close = () => {
    setSession(null);
    onOpenChange?.(false);
  };
  const systemLabel = t("review.parity.systemNaming");
  const customLabel = t("review.naming.custom", { defaultValue: "Custom rule · this plan only" });
  const ruleItems = [
    { value: "system", label: systemLabel },
    { value: "custom", label: customLabel },
  ];
  const campaignScopeItems = [
    {
      value: "all",
      label: t(
        session?.selectedCampaignKeys?.size
          ? "review.naming.selectedCampaigns"
          : "review.naming.allCampaigns",
      ),
    },
    ...scopeCampaigns.map((campaign) => ({
      value: campaign.ref.lineageKey,
      label: campaign.name.effective,
    })),
  ];
  const entityScopeItems = [
    { value: "all", label: t("review.naming.campaignsAndGroups") },
    { value: "campaigns", label: t("review.naming.campaignNamesOnly") },
    ...scopeGroups.map((group) => ({
      value: group.ref.lineageKey,
      label: t("review.naming.singleGroup", { name: group.name.effective }),
    })),
  ];
  const issueText = (issue: NameIssue | null) =>
    issue
      ? t(`review.naming.errors.${issue}`, {
          defaultValue: {
            empty: "Enter a nonempty name.",
            tooLong: "Generated names must be 128 characters or fewer.",
            unknownToken: "Use only the supported placeholders.",
            duplicate: "Names must be unique within their scope.",
          }[issue],
        })
      : undefined;
  const blocked = disabled || pending || workspace.status !== "ready";
  const changeCount = (preview?.changes.length ?? 0) + (ruleChanged ? 1 : 0);
  const tooManyChanges = changeCount > 100;
  const namingCommand: SmartCreationReviewEditChange = {
    operation: "set_naming_rule",
    ref: workspace.planSettings.ref,
    namingRule: requestedRule,
  };
  return (
    <>
      {controlledOpen === undefined && (
        <>
          <Select
            value={selectedRule}
            items={ruleItems}
            disabled={blocked}
            onValueChange={(value) => {
              if (value === "system" || value === "custom") open(value);
            }}
          >
            <SelectTrigger size="sm" aria-label={t("review.parity.namingRule")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {ruleItems.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" disabled={blocked} onClick={() => open("custom")}>
            {t("review.naming.manage", { defaultValue: "Manage naming templates" })}
          </Button>
        </>
      )}
      <Dialog
        open={session !== null}
        onOpenChange={(value) => {
          if (!value && !pending) close();
        }}
      >
        <DialogPopup
          className="smart-creation-preview max-w-[640px] border border-line"
          data-review-naming-dialog
        >
          <DialogHeader className="border-b border-line px-6 py-5">
            <DialogTitle className="text-base leading-[22px]">
              {t("review.naming.title", { defaultValue: "Naming rules" })}
            </DialogTitle>
            <DialogDescription>
              {t("review.naming.scope", {
                defaultValue: "Templates are saved in this plan, not as Workspace defaults.",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="py-5 in-[[data-slot=dialog-popup]:has([data-slot=dialog-header])]:pt-5">
            <FieldGroup>
              <FieldGroup className="flex-row flex-wrap gap-3">
                <Field className="min-w-0 flex-1 basis-52">
                  <FieldLabel htmlFor="review-naming-campaign-scope">
                    {t("review.naming.campaignScope")}
                  </FieldLabel>
                  <Select
                    value={session?.campaignScope ?? "all"}
                    items={campaignScopeItems}
                    disabled={pending}
                    onValueChange={(value) => {
                      if (
                        typeof value === "string" &&
                        campaignScopeItems.some((item) => item.value === value)
                      )
                        setSession((current) =>
                          current
                            ? { ...current, campaignScope: value, entityScope: "all" }
                            : current,
                        );
                    }}
                  >
                    <SelectTrigger id="review-naming-campaign-scope" size="sm" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {campaignScopeItems.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
                <Field className="min-w-0 flex-1 basis-52">
                  <FieldLabel htmlFor="review-naming-entity-scope">
                    {t("review.naming.entityScope")}
                  </FieldLabel>
                  <Select
                    value={session?.entityScope ?? "all"}
                    items={entityScopeItems}
                    disabled={pending}
                    onValueChange={(value) => {
                      if (
                        typeof value === "string" &&
                        entityScopeItems.some((item) => item.value === value)
                      )
                        setSession((current) =>
                          current ? { ...current, entityScope: value } : current,
                        );
                    }}
                  >
                    <SelectTrigger id="review-naming-entity-scope" size="sm" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {entityScopeItems.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
              </FieldGroup>
              <p className="text-xs text-muted-foreground">{t("review.naming.scopeHint")}</p>
              <Field>
                <FieldLabel>{t("review.parity.namingRule")}</FieldLabel>
                <Select
                  value={session?.rule ?? "custom"}
                  items={ruleItems}
                  disabled={pending}
                  onValueChange={(value) => {
                    if (value === "system" || value === "custom")
                      setSession((current) => (current ? { ...current, rule: value } : current));
                  }}
                >
                  <SelectTrigger size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {ruleItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
              {session?.rule === "custom" && (
                <>
                  <Field invalid={Boolean(preview?.templateErrors.campaign)}>
                    <FieldLabel htmlFor="review-naming-campaign">
                      {t("review.naming.campaignTemplate", {
                        defaultValue: "Campaign name template",
                      })}
                    </FieldLabel>
                    <Input
                      id="review-naming-campaign"
                      size="sm"
                      maxLength={500}
                      value={template.campaign}
                      disabled={pending}
                      aria-invalid={Boolean(preview?.templateErrors.campaign)}
                      onChange={(event) =>
                        setTemplate((current) => ({ ...current, campaign: event.target.value }))
                      }
                    />
                    <FieldError match={Boolean(preview?.templateErrors.campaign)}>
                      {issueText(preview?.templateErrors.campaign ?? null)}
                    </FieldError>
                  </Field>
                  <Field invalid={Boolean(preview?.templateErrors.adGroup)}>
                    <FieldLabel htmlFor="review-naming-group">
                      {t("review.naming.adGroupTemplate", {
                        defaultValue: "Ad Group name template",
                      })}
                    </FieldLabel>
                    <Input
                      id="review-naming-group"
                      size="sm"
                      maxLength={500}
                      value={template.adGroup}
                      disabled={pending}
                      aria-invalid={Boolean(preview?.templateErrors.adGroup)}
                      onChange={(event) =>
                        setTemplate((current) => ({ ...current, adGroup: event.target.value }))
                      }
                    />
                    <FieldDescription>
                      {t("review.naming.tokens", {
                        defaultValue:
                          "Placeholders: {sku}, {asin}, {strategy}, {match}, {adGroup} (initial name), {counter}. Multiple values are joined with +; counters run separately for Campaigns and Ad Groups.",
                      })}
                    </FieldDescription>
                    <FieldError match={Boolean(preview?.templateErrors.adGroup)}>
                      {issueText(preview?.templateErrors.adGroup ?? null)}
                    </FieldError>
                  </Field>
                </>
              )}
              <Field orientation="horizontal">
                <Checkbox
                  id="review-naming-overwrite"
                  checked={overwriteEdited}
                  disabled={pending}
                  onCheckedChange={setOverwriteEdited}
                />
                <FieldLabel htmlFor="review-naming-overwrite">
                  {t("review.naming.overwrite", {
                    defaultValue: "Also replace names I have edited",
                  })}
                </FieldLabel>
              </Field>
              {stale && (
                <Field invalid>
                  <FieldError match>
                    {t("review.naming.stale", {
                      defaultValue:
                        "The plan changed while this preview was open. Refresh the preview before applying.",
                    })}
                  </FieldError>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pending}
                    onClick={() =>
                      setSession((current) =>
                        current ? { ...current, snapshot: workspace } : current,
                      )
                    }
                  >
                    {t("review.naming.refresh", { defaultValue: "Refresh preview" })}
                  </Button>
                </Field>
              )}
              {(preview?.tooManyChanges || tooManyChanges) && (
                <Field invalid>
                  <FieldError match>
                    {t("review.naming.tooMany", {
                      defaultValue:
                        "At most 100 changes can be applied at once. Narrow the scope above to a Campaign or an individual name.",
                      count: changeCount,
                    })}
                  </FieldError>
                </Field>
              )}
              <div className="max-h-64 overflow-auto rounded-md border border-line">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("review.naming.entity", { defaultValue: "Item" })}</TableHead>
                      <TableHead>
                        {t("review.naming.current", { defaultValue: "Current name" })}
                      </TableHead>
                      <TableHead>
                        {t("review.naming.preview", { defaultValue: "Preview" })}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview?.rows.map((row) => (
                      <TableRow
                        key={row.ref.lineageKey}
                        data-review-naming-row={row.ref.lineageKey}
                      >
                        <TableCell>
                          {row.ref.kind === "campaign"
                            ? t("review.naming.campaign", { defaultValue: "Campaign" })
                            : t("review.naming.adGroup", { defaultValue: "Ad Group" })}
                        </TableCell>
                        <TableCell className="max-w-64 whitespace-normal break-words">
                          {row.current}
                        </TableCell>
                        <TableCell className="max-w-64 whitespace-normal break-words">
                          <span>{row.next}</span>
                          {row.preserved && (
                            <Badge variant="neutral">
                              {t("review.naming.preserved", { defaultValue: "Kept" })}
                            </Badge>
                          )}
                          {row.issue && (
                            <p className="text-xs text-destructive-foreground">
                              {issueText(row.issue)}
                            </p>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </FieldGroup>
          </DialogPanel>
          <DialogFooter className="min-h-16">
            <Button variant="outline" size="sm" disabled={pending} onClick={close}>
              {t("review.naming.cancel", { defaultValue: "Cancel" })}
            </Button>
            {session?.rule === "custom" && (
              <Button
                variant="outline"
                size="sm"
                disabled={
                  blocked ||
                  stale ||
                  Boolean(preview?.templateErrors.campaign || preview?.templateErrors.adGroup) ||
                  template.campaign.length > 500 ||
                  template.adGroup.length > 500
                }
                onClick={async () => {
                  if (
                    stale ||
                    blocked ||
                    templateIssue(template.campaign) ||
                    templateIssue(template.adGroup) ||
                    template.campaign.length > 500 ||
                    template.adGroup.length > 500
                  )
                    return;
                  const nextTemplate = { ...template };
                  setPending(true);
                  try {
                    const success = await onApply([
                      {
                        operation: "set_naming_rule",
                        ref: workspace.planSettings.ref,
                        namingRule: {
                          selectedRule: persistedRule.selectedRule,
                          customTemplate: nextTemplate,
                        },
                      },
                    ]);
                    if (success !== false) {
                      setSavedTemplate(nextTemplate);
                      close();
                    }
                  } catch {
                    toast.error(t("review.naming.applyFailed"));
                  } finally {
                    setPending(false);
                  }
                }}
              >
                {t("review.naming.save", { defaultValue: "Save template for this plan" })}
              </Button>
            )}
            <Button
              size="sm"
              disabled={
                blocked ||
                stale ||
                !preview ||
                preview.hasErrors ||
                tooManyChanges ||
                changeCount === 0
              }
              onClick={async () => {
                if (!preview || preview.hasErrors || tooManyChanges || stale || blocked) return;
                setPending(true);
                try {
                  const success = await onApply([
                    ...(ruleChanged ? [namingCommand] : []),
                    ...preview.changes,
                  ]);
                  if (success !== false) {
                    if (session?.rule === "custom") setSavedTemplate({ ...template });
                    setSelectedRule(session?.rule ?? "system");
                    close();
                  }
                } catch {
                  toast.error(
                    t("review.naming.applyFailed", {
                      defaultValue: "Could not apply the naming rule. Try again.",
                    }),
                  );
                } finally {
                  setPending(false);
                }
              }}
            >
              {pending && <Spinner data-icon="inline-start" />}
              {t("review.naming.apply", {
                defaultValue: "Apply names",
                count: preview?.changes.length ?? 0,
              })}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </>
  );
}
